import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Tab, Tabs, Modal, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import FeatureNotice from './FeatureNotice';
import OrgPrintChrome from './OrgPrintChrome';
import JobWork from './JobWork';
import useSectionTab from '../hooks/useSectionTab';
import { todayISO } from '../utils/dates';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
import { formatDateTime, formatDate } from '../utils/dateFormat';
// Section keys, in tab order; the first is where a first visit opens
const PRODUCTION_SECTIONS = ['boms', 'work-orders', 'job-work'];

function Production() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  // The open section is in the address (/production/<section>)
  const [activeTab, setActiveTab] = useSectionTab('/production', PRODUCTION_SECTIONS);

  // BOM state
  const [boms, setBoms] = useState([]);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]); // Products created by Org Admin
  const [uoms, setUoms] = useState([]);
  const [productionUnits, setProductionUnits] = useState([]);
  const [showBOMModal, setShowBOMModal] = useState(false);
  const [editingBOM, setEditingBOM] = useState(null);
  const [bomFormData, setBomFormData] = useState({
    bomNumber: '',
    bomName: '',
    description: '',
    finishedGoodItemId: '',
    productionUnitId: '',
    version: '1.0',
    isActive: true,
    isDefault: false
  });
  const [bomLineItems, setBomLineItems] = useState([{
    itemId: '',
    unitOfMeasurementId: '',
    quantity: '',
    wastagePercentage: '',
    notes: ''
  }]);
  const [bomOutputProducts, setBomOutputProducts] = useState([{
    itemId: '',
    outputType: 'PRIMARY',
    baseQuantity: '',
    unitOfMeasurementId: ''
  }]);

  // Work Order state
  const [workOrders, setWorkOrders] = useState([]);
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [editingWorkOrder, setEditingWorkOrder] = useState(null);
  const [workOrderFormData, setWorkOrderFormData] = useState({
    workOrderNumber: '',
    workOrderDate: todayISO(),
    bomId: '',
    productionUnitId: '',
    quantityToProduce: '',
    unitOfMeasurementId: '',
    quantityProduced: '0',
    startDate: '',
    completionDate: '',
    status: 'DRAFT',
    priority: 'NORMAL',
    notes: ''
  });
  const [showWorkOrderStatus, setShowWorkOrderStatus] = useState(false);
  const [selectedWorkOrderForStatus, setSelectedWorkOrderForStatus] = useState(null);
  const [workOrderStatusHistory, setWorkOrderStatusHistory] = useState([]);
  const [statusTransitionData, setStatusTransitionData] = useState({
    toStatus: '',
    comment: ''
  });

  // Help pane state

  // Print view state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printType, setPrintType] = useState(null); // 'BOM' | 'WORK_ORDER'
  const [workOrderTransactions, setWorkOrderTransactions] = useState({ gins: [], grns: [] });
  const [loadingWorkOrderTransactions, setLoadingWorkOrderTransactions] = useState(false);
  // Per-item issued/received totals for the Material Consumption table in the Edit Work Order modal
  const [editWorkOrderTotals, setEditWorkOrderTotals] = useState({ issued: {}, received: {} });
  // Whether the work order being edited already has material issued/received (locks Production Unit + WO date)
  const [editWorkOrderHasActivity, setEditWorkOrderHasActivity] = useState(false);
  const [editWorkOrderEarliestMovement, setEditWorkOrderEarliestMovement] = useState('');
  const [workOrderCancelBlocked, setWorkOrderCancelBlocked] = useState(false);
  const [workOrderInProgressBlocked, setWorkOrderInProgressBlocked] = useState(false);
  const [workOrderPrintStatusHistory, setWorkOrderPrintStatusHistory] = useState([]);
  const [completionData, setCompletionData] = useState({
    quantityProduced: '',
    lineItems: [],
    laborCostPerUnit: '',
    overheadCostPerUnit: ''
  });

  // Organization Materials state
  const [organizationMaterials, setOrganizationMaterials] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialFormData, setMaterialFormData] = useState({
    itemId: '',
    sku: '',
    moq: '',
    ssq: '',
    rop: '',
    poq: ''
  });

  useEffect(() => {
    if (activeTab === 'boms') {
      fetchBOMs();
      fetchItems();
      fetchProducts(); // Fetch organization products
      fetchOrganizationMaterials(); // Fetch organization materials for BOM line items
      fetchUOMs();
      fetchProductionUnits();
    } else if (activeTab === 'work-orders') {
      fetchWorkOrders();
      fetchBOMs();
      fetchProductionUnits();
      fetchUOMs(); // Fetch UOMs for Work Order UOM dropdown
    } else if (activeTab === 'materials') {
      fetchOrganizationMaterials();
      fetchAllItems();
    }
  }, [activeTab]);

  // Fetch GIN/GRN transactions when opening Work Order print view
  useEffect(() => {
    if (!showPrintModal || printType !== 'WORK_ORDER' || !printData?.id) {
      setWorkOrderTransactions({ gins: [], grns: [] });
      setWorkOrderPrintStatusHistory([]);
      return;
    }
    let cancelled = false;
    const fetchTransactions = async () => {
      setLoadingWorkOrderTransactions(true);
      try {
        const [txRes, historyRes] = await Promise.all([
          axios.get(`/api/production/work-orders/${printData.id}/transactions`),
          axios.get(`/api/production/work-orders/${printData.id}/status-history`)
        ]);
        if (!cancelled && txRes.data) {
          setWorkOrderTransactions({
            gins: txRes.data.gins || [],
            grns: txRes.data.grns || []
          });
        }
        if (!cancelled) {
          setWorkOrderPrintStatusHistory(historyRes.data || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching work order transactions:', err);
          setWorkOrderTransactions({ gins: [], grns: [] });
          setWorkOrderPrintStatusHistory([]);
        }
      } finally {
        if (!cancelled) setLoadingWorkOrderTransactions(false);
      }
    };
    fetchTransactions();
    return () => { cancelled = true; };
  }, [showPrintModal, printType, printData?.id]);

  // BOM functions
  const fetchBOMs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/production/boms');
      setBoms(response.data);
    } catch (error) {
      toast.error('Error fetching BOMs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get('/api/inventory/items');
      setItems(response.data.filter(item => item.isActive));
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products');
      setProducts(response.data.filter(product => product.isActive && product.item));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchUOMs = async () => {
    try {
      const response = await axios.get('/api/inventory/units-of-measurement');
      setUoms(response.data);
    } catch (error) {
      console.error('Error fetching UOMs:', error);
    }
  };

  const fetchProductionUnits = async () => {
    try {
      const response = await axios.get('/api/inventory/production-units/active');
      setProductionUnits(response.data);
    } catch (error) {
      console.error('Error fetching production units:', error);
    }
  };

  // Organization Materials functions
  const fetchOrganizationMaterials = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/organization-materials');
      setOrganizationMaterials(response.data);
    } catch (error) {
      toast.error('Error fetching organization materials');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllItems = async () => {
    try {
      const response = await axios.get('/api/inventory/items');
      setAllItems(response.data.filter(item => item.isActive));
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const handleCreateMaterial = () => {
    setMaterialFormData({
      itemId: '',
      sku: '',
      moq: '',
      ssq: '',
      rop: '',
      poq: ''
    });
    setEditingMaterial(null);
    setShowMaterialModal(true);
  };

  const handleEditMaterial = async (itemId) => {
    try {
      // Try to fetch existing organization material for this item
      const response = await axios.get(`/api/organization-materials/item/${itemId}`);
      const orgMaterial = response.data;
      setEditingMaterial(orgMaterial);
      setMaterialFormData({
        itemId: orgMaterial.item?.id || itemId,
        sku: orgMaterial.sku || '',
        moq: orgMaterial.moq || '',
        ssq: orgMaterial.ssq || '',
        rop: orgMaterial.rop || '',
        poq: orgMaterial.poq || ''
      });
      setShowMaterialModal(true);
    } catch (error) {
      // If not found, create new
      const item = allItems.find(i => i.id === itemId);
      setEditingMaterial(null);
      setMaterialFormData({
        itemId: itemId,
        sku: item?.sku || '',
        moq: '',
        ssq: '',
        rop: '',
        poq: ''
      });
      setShowMaterialModal(true);
    }
  };

  const handleMaterialInputChange = (e) => {
    setMaterialFormData({
      ...materialFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        item: { id: parseInt(materialFormData.itemId) },
        sku: materialFormData.sku || null,
        moq: materialFormData.moq ? parseFloat(materialFormData.moq) : null,
        ssq: materialFormData.ssq ? parseFloat(materialFormData.ssq) : null,
        rop: materialFormData.rop ? parseFloat(materialFormData.rop) : null,
        poq: materialFormData.poq ? parseFloat(materialFormData.poq) : null
      };

      if (editingMaterial) {
        await axios.put(`/api/organization-materials/${editingMaterial.id}`, payload);
        toast.success('Material attributes updated successfully');
      } else {
        await axios.post('/api/organization-materials', payload);
        toast.success('Material attributes saved successfully');
      }
      setShowMaterialModal(false);
      setEditingMaterial(null);
      fetchOrganizationMaterials();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving material attributes';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving material attributes');
    }
  };

  const handleDeleteMaterial = async (id) => {
    if (window.confirm('Are you sure you want to delete these material attributes?')) {
      try {
        await axios.delete(`/api/organization-materials/${id}`);
        toast.success('Material attributes deleted successfully');
        fetchOrganizationMaterials();
      } catch (error) {
        const errorMsg = error.response?.data || 'Error deleting material attributes';
        toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting material attributes');
      }
    }
  };

  const handleCloseMaterialModal = () => {
    setShowMaterialModal(false);
    setEditingMaterial(null);
    setMaterialFormData({
      itemId: '',
      sku: '',
      moq: '',
      ssq: '',
      rop: '',
      poq: ''
    });
  };

  const handleCreateBOM = () => {
    setBomFormData({
      bomNumber: '',
      bomName: '',
      description: '',
      finishedGoodItemId: '',
      productionUnitId: '',
      version: '1.0',
      isActive: true,
      isDefault: false
    });
    setBomLineItems([{
      itemId: '',
      unitOfMeasurementId: '',
      quantity: '',
      wastagePercentage: '',
      notes: ''
    }]);
    setBomOutputProducts([{
      itemId: '',
      outputType: 'PRIMARY',
      baseQuantity: '',
      unitOfMeasurementId: ''
    }]);
    setEditingBOM(null);
    setShowBOMModal(true);
  };

  const handleViewPrint = (data, type) => {
    setPrintData(data);
    setPrintType(type);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEditBOM = (bom) => {
    setEditingBOM(bom);
    setBomFormData({
      bomNumber: bom.bomNumber,
      bomName: bom.bomName,
      description: bom.description || '',
      finishedGoodItemId: bom.finishedGoodItem?.id || '',
      productionUnitId: bom.productionUnit?.id || '',
      version: bom.version || '1.0',
      isActive: bom.isActive !== undefined ? bom.isActive : true,
      isDefault: bom.isDefault !== undefined ? bom.isDefault : false
    });
    if (bom.lineItems && bom.lineItems.length > 0) {
      setBomLineItems(bom.lineItems.map(li => ({
        itemId: li.item?.id || '',
        unitOfMeasurementId: li.unitOfMeasurement?.id || '',
        quantity: li.quantity || '',
        wastagePercentage: li.wastagePercentage || '',
        notes: li.notes || ''
      })));
    } else {
      setBomLineItems([{
        itemId: '',
        unitOfMeasurementId: '',
        quantity: '',
        wastagePercentage: '',
        notes: ''
      }]);
    }
    // Load output products if they exist, otherwise use legacy finishedGoodItem
    if (bom.outputProducts && bom.outputProducts.length > 0) {
      setBomOutputProducts(bom.outputProducts.map(op => ({
        itemId: op.item?.id || '',
        outputType: op.outputType || 'PRIMARY',
        baseQuantity: op.baseQuantity || '',
        unitOfMeasurementId: op.unitOfMeasurement?.id || ''
      })));
    } else if (bom.finishedGoodItem) {
      // Backward compatibility: convert legacy finishedGoodItem to output product
      setBomOutputProducts([{
        itemId: bom.finishedGoodItem.id || '',
        outputType: 'PRIMARY',
        baseQuantity: '1.000', // Default base quantity
        unitOfMeasurementId: ''
      }]);
    } else {
      setBomOutputProducts([{
        itemId: '',
        outputType: 'PRIMARY',
        baseQuantity: '',
        unitOfMeasurementId: ''
      }]);
    }
    setShowBOMModal(true);
  };

  const handleDeleteBOM = async (id) => {
    if (window.confirm('Are you sure you want to delete this BOM?')) {
      try {
        await axios.delete(`/api/production/boms/${id}`);
        toast.success('BOM deleted successfully');
        fetchBOMs();
      } catch (error) {
        const errorMsg = error.response?.data || 'Error deleting BOM';
        toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting BOM');
      }
    }
  };

  const handleAddBOMLineItem = () => {
    setBomLineItems([...bomLineItems, {
      itemId: '',
      unitOfMeasurementId: '',
      quantity: '',
      wastagePercentage: '',
      notes: ''
    }]);
  };

  const handleRemoveBOMLineItem = (index) => {
    setBomLineItems(bomLineItems.filter((_, i) => i !== index));
  };

  const handleBOMLineItemChange = (index, field, value) => {
    const updated = [...bomLineItems];
    updated[index] = { ...updated[index], [field]: value };
    setBomLineItems(updated);
  };

  const handleAddBOMOutputProduct = () => {
    setBomOutputProducts([...bomOutputProducts, {
      itemId: '',
      outputType: 'PRIMARY',
      baseQuantity: '',
      unitOfMeasurementId: ''
    }]);
  };

  const handleRemoveBOMOutputProduct = (index) => {
    setBomOutputProducts(bomOutputProducts.filter((_, i) => i !== index));
  };

  const handleBOMOutputProductChange = (index, field, value) => {
    const updated = [...bomOutputProducts];
    updated[index] = { ...updated[index], [field]: value };
    setBomOutputProducts(updated);
  };

  const handleBOMSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate at least one PRIMARY output product
      const primaryProducts = bomOutputProducts.filter(op => op.itemId && op.outputType === 'PRIMARY');
      if (primaryProducts.length === 0) {
        toast.error('At least one PRIMARY output product is required');
        return;
      }

      const payload = {
        ...bomFormData,
        finishedGoodItem: null, // No longer using legacy field
        productionUnit: bomFormData.productionUnitId ? { id: parseInt(bomFormData.productionUnitId) } : null,
        outputProducts: bomOutputProducts
          .filter(op => op.itemId && op.baseQuantity)
          .map(op => ({
            item: { id: parseInt(op.itemId) },
            outputType: op.outputType,
            baseQuantity: parseFloat(op.baseQuantity),
            unitOfMeasurement: op.unitOfMeasurementId ? { id: parseInt(op.unitOfMeasurementId) } : null
          })),
        lineItems: bomLineItems
          .filter(li => li.itemId && li.quantity)
          .map(li => ({
            item: { id: parseInt(li.itemId) },
            unitOfMeasurement: li.unitOfMeasurementId ? { id: parseInt(li.unitOfMeasurementId) } : null,
            quantity: parseFloat(li.quantity),
            wastagePercentage: li.wastagePercentage ? parseFloat(li.wastagePercentage) : null,
            notes: li.notes || null
          }))
      };
      delete payload.finishedGoodItemId;
      delete payload.productionUnitId;

      if (editingBOM) {
        await axios.put(`/api/production/boms/${editingBOM.id}`, payload);
        toast.success('BOM updated successfully');
      } else {
        await axios.post('/api/production/boms', payload);
        toast.success('BOM created successfully');
      }
      setShowBOMModal(false);
      setEditingBOM(null);
      fetchBOMs();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving BOM';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving BOM');
    }
  };

  const handleCloseBOMModal = () => {
    setShowBOMModal(false);
    setEditingBOM(null);
    setBomFormData({
      bomNumber: '',
      bomName: '',
      description: '',
      finishedGoodItemId: '',
      productionUnitId: '',
      version: '1.0',
      isActive: true,
      isDefault: false
    });
    setBomLineItems([{
      itemId: '',
      unitOfMeasurementId: '',
      quantity: '',
      wastagePercentage: '',
      notes: ''
    }]);
    setBomOutputProducts([{
      itemId: '',
      outputType: 'PRIMARY',
      baseQuantity: '',
      unitOfMeasurementId: ''
    }]);
  };

  // Work Order functions
  const fetchWorkOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/production/work-orders');
      setWorkOrders(response.data);
    } catch (error) {
      toast.error('Error fetching work orders');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkOrder = () => {
    setWorkOrderFormData({
      workOrderNumber: '',
      workOrderDate: todayISO(),
      bomId: '',
      productionUnitId: '',
      quantityToProduce: '',
      unitOfMeasurementId: '',
      quantityProduced: '0',
      startDate: '',
      completionDate: '',
      status: 'DRAFT',
      priority: 'NORMAL',
      notes: ''
    });
    setEditingWorkOrder(null);
    setShowWorkOrderModal(true);
  };

  // Sum issued (from stock-reducing GINs) and received (from non-voided GRNs) per item id
  const computeIssuedReceivedByItem = (txData) => {
    const issued = {};
    (txData?.gins || []).forEach((gin) => {
      const status = (gin?.status || '').toUpperCase();
      if (status !== 'ISSUED' && status !== 'PARTIAL' && status !== 'COMPLETED') return;
      (gin.lineItems || []).forEach((li) => {
        if (!li?.itemId) return;
        const qty = li.issuedQuantity != null ? Number(li.issuedQuantity) : Number(li.quantity);
        if (!Number.isFinite(qty)) return;
        issued[li.itemId] = (issued[li.itemId] || 0) + qty;
      });
    });
    const received = {};
    (txData?.grns || []).forEach((grn) => {
      if (!grn || ['CANCELLED', 'REVERSED'].includes((grn.status || '').toUpperCase())) return;
      (grn.lineItems || []).forEach((li) => {
        if (!li?.itemId) return;
        const qty = li.receivedQuantity != null ? Number(li.receivedQuantity) : Number(li.quantity);
        if (!Number.isFinite(qty)) return;
        received[li.itemId] = (received[li.itemId] || 0) + qty;
      });
    });
    return { issued, received };
  };

  const handleEditWorkOrder = async (wo) => {
    setEditingWorkOrder(wo);
    setWorkOrderFormData({
      workOrderNumber: wo.workOrderNumber,
      workOrderDate: wo.workOrderDate,
      bomId: wo.bom?.id || '',
      productionUnitId: wo.productionUnit?.id || '',
      quantityToProduce: wo.quantityToProduce || '',
      unitOfMeasurementId: wo.unitOfMeasurement?.id || '',
      quantityProduced: wo.quantityProduced || '0',
      startDate: wo.startDate || '',
      completionDate: wo.completionDate || '',
      status: wo.status || 'DRAFT',
      priority: wo.priority || 'NORMAL',
      notes: wo.notes || ''
    });
    setEditWorkOrderTotals({ issued: {}, received: {} });
    setEditWorkOrderHasActivity(false);
    setEditWorkOrderEarliestMovement('');
    setShowWorkOrderModal(true);

    // Issued/Received are derived from GIN/GRN transactions, not stored on the WO line items
    try {
      const txRes = await axios.get(`/api/production/work-orders/${wo.id}/transactions`);
      const txData = txRes.data || {};
      setEditWorkOrderTotals(computeIssuedReceivedByItem(txData));
      setEditWorkOrderHasActivity(hasActiveWorkOrderStockDocs(txData));
      setEditWorkOrderEarliestMovement(earliestMaterialMovementDate(txData));
    } catch (error) {
      console.error('Error fetching work order transactions for edit view:', error);
      setEditWorkOrderTotals({ issued: {}, received: {} });
      setEditWorkOrderHasActivity(false);
      setEditWorkOrderEarliestMovement('');
    }
  };

  // Earliest date material moved (issued GIN or non-voided GRN) against the work order
  const earliestMaterialMovementDate = (txData) => {
    let earliest = '';
    (txData?.gins || []).forEach((gin) => {
      if (!gin || (gin.status || '').toUpperCase() === 'CANCELLED') return;
      if (gin.ginDate && (!earliest || gin.ginDate < earliest)) earliest = gin.ginDate;
    });
    (txData?.grns || []).forEach((grn) => {
      if (!grn || ['CANCELLED', 'REVERSED'].includes((grn.status || '').toUpperCase())) return;
      if (grn.grnDate && (!earliest || grn.grnDate < earliest)) earliest = grn.grnDate;
    });
    return earliest;
  };

  const handleDeleteWorkOrder = async (id) => {
    if (window.confirm('Are you sure you want to delete this work order?')) {
      try {
        await axios.delete(`/api/production/work-orders/${id}`);
        toast.success('Work order deleted successfully');
        fetchWorkOrders();
      } catch (error) {
        const errorMsg = error.response?.data || 'Error deleting work order';
        toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting work order');
      }
    }
  };

  const handleWorkOrderSubmit = async (e) => {
    e.preventDefault();
    const today = todayISO();
    if (workOrderFormData.workOrderDate && workOrderFormData.workOrderDate > today) {
      toast.error('Work order date cannot be in the future');
      return;
    }
    if (
      editingWorkOrder &&
      editWorkOrderHasActivity &&
      editWorkOrderEarliestMovement &&
      workOrderFormData.workOrderDate &&
      workOrderFormData.workOrderDate > editWorkOrderEarliestMovement
    ) {
      toast.error(`Work order date cannot be after the first material movement (${editWorkOrderEarliestMovement})`);
      return;
    }
    try {
      const payload = {
        ...workOrderFormData,
        bom: workOrderFormData.bomId ? { id: parseInt(workOrderFormData.bomId) } : null,
        productionUnit: workOrderFormData.productionUnitId ? { id: parseInt(workOrderFormData.productionUnitId) } : null,
        unitOfMeasurement: workOrderFormData.unitOfMeasurementId ? { id: parseInt(workOrderFormData.unitOfMeasurementId) } : null,
        quantityToProduce: parseFloat(workOrderFormData.quantityToProduce),
        startDate: workOrderFormData.startDate || null,
        completionDate: workOrderFormData.completionDate || null
      };
      delete payload.bomId;
      delete payload.productionUnitId;
      delete payload.unitOfMeasurementId;
      delete payload.quantityProduced;

      if (editingWorkOrder) {
        await axios.put(`/api/production/work-orders/${editingWorkOrder.id}`, payload);
        toast.success('Work order updated successfully');
      } else {
        await axios.post('/api/production/work-orders', payload);
        toast.success('Work order created successfully');
      }
      setShowWorkOrderModal(false);
      setEditingWorkOrder(null);
      fetchWorkOrders();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving work order';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving work order');
    }
  };

  const handleCloseWorkOrderModal = () => {
    setShowWorkOrderModal(false);
    setEditingWorkOrder(null);
    setEditWorkOrderTotals({ issued: {}, received: {} });
    setEditWorkOrderHasActivity(false);
    setEditWorkOrderEarliestMovement('');
    setWorkOrderFormData({
      workOrderNumber: '',
      workOrderDate: todayISO(),
      bomId: '',
      productionUnitId: '',
      quantityToProduce: '',
      unitOfMeasurementId: '',
      quantityProduced: '0',
      startDate: '',
      completionDate: '',
      status: 'DRAFT',
      priority: 'NORMAL',
      notes: ''
    });
  };

  const getAllowedStatusTransitions = (currentStatus, options = {}) => {
    const transitions = {
      DRAFT: ['APPROVED', 'CANCELLED'],
      APPROVED: ['IN_PROGRESS', 'CANCELLED'],
      IN_PROGRESS: ['COMPLETED', 'CANCELLED'],
      COMPLETED: [],
      CANCELLED: []
    };
    let allowed = transitions[currentStatus] || [];
    if (options.blockCancel) {
      allowed = allowed.filter((status) => status !== 'CANCELLED');
    }
    if (options.blockInProgress) {
      allowed = allowed.filter((status) => status !== 'IN_PROGRESS');
    }
    return allowed;
  };

  const hasActiveWorkOrderStockDocs = (txData) => {
    const gins = txData?.gins || [];
    const grns = txData?.grns || [];
    const isVoided = (s) => ['CANCELLED', 'REVERSED'].includes((s || '').toUpperCase());
    const activeGin = gins.some((g) => (g.status || '').toUpperCase() !== 'CANCELLED');
    const activeGrn = grns.some((g) => !isVoided(g.status));
    return activeGin || activeGrn;
  };

  const hasProductionUnitReceiptForWorkOrder = (txData, workOrder) => {
    const productionUnitId = workOrder?.productionUnit?.id != null
      ? String(workOrder.productionUnit.id)
      : null;
    if (!productionUnitId) return false;
    return (txData?.grns || []).some((grn) => {
      if (!grn || ['CANCELLED', 'REVERSED'].includes((grn.status || '').toUpperCase())) return false;
      if (grn.productionUnitId == null) return false;
      return String(grn.productionUnitId) === productionUnitId;
    });
  };

  const formatQty3 = (value) => {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '-';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  };

  /** Material balance for Status Workflow: issued / received at PU / consumed / remaining. */
  const getWorkOrderMaterialStatusSummary = () => {
    const workOrder = selectedWorkOrderForStatus;
    if (!workOrder) {
      return { rows: [], grnsAtPu: [] };
    }
    const productionUnitId = workOrder.productionUnit?.id != null
      ? String(workOrder.productionUnit.id)
      : null;

    const issuedByItemId = {};
    (workOrderTransactions.gins || []).forEach((gin) => {
      const status = (gin.status || '').toUpperCase();
      if (status !== 'ISSUED' && status !== 'PARTIAL' && status !== 'COMPLETED') return;
      (gin.lineItems || []).forEach((li) => {
        if (!li?.itemId) return;
        const qty = li.issuedQuantity != null ? Number(li.issuedQuantity) : Number(li.quantity);
        if (!Number.isFinite(qty)) return;
        const key = String(li.itemId);
        issuedByItemId[key] = (issuedByItemId[key] || 0) + qty;
      });
    });

    const receivedByItemId = {};
    const grnsAtPu = [];
    (workOrderTransactions.grns || []).forEach((grn) => {
      if (!grn || ['CANCELLED', 'REVERSED'].includes((grn.status || '').toUpperCase())) return;
      if (productionUnitId && (grn.productionUnitId == null || String(grn.productionUnitId) !== productionUnitId)) {
        return;
      }
      if (!productionUnitId && grn.productionUnitId == null) return;
      grnsAtPu.push(grn);
      (grn.lineItems || []).forEach((li) => {
        if (!li?.itemId) return;
        if ((li.lineType || 'MATERIAL').toString().toUpperCase() !== 'MATERIAL') return;
        const qty = li.receivedQuantity != null ? Number(li.receivedQuantity) : Number(li.quantity);
        if (!Number.isFinite(qty)) return;
        const key = String(li.itemId);
        receivedByItemId[key] = (receivedByItemId[key] || 0) + qty;
      });
    });

    const rows = (workOrder.lineItems || [])
      .filter((li) => li.item?.id)
      .map((li) => {
        const key = String(li.item.id);
        const required = li.requiredQuantity != null ? Number(li.requiredQuantity) : 0;
        const issued = issuedByItemId[key] || 0;
        const received = receivedByItemId[key] || 0;
        const consumed = li.consumedQuantity != null ? Number(li.consumedQuantity) : 0;
        const wastage = li.wastageQuantity != null ? Number(li.wastageQuantity) : 0;
        const remaining = Math.max(0, received - (Number.isFinite(consumed) ? consumed : 0) - (Number.isFinite(wastage) ? wastage : 0));
        return {
          itemId: li.item.id,
          itemName: li.item?.name || '-',
          uomSymbol: li.unitOfMeasurement?.symbol || li.unitOfMeasurement?.name || '',
          required: Number.isFinite(required) ? required : 0,
          issued,
          received,
          consumed: Number.isFinite(consumed) ? consumed : 0,
          wastage: Number.isFinite(wastage) ? wastage : 0,
          remaining
        };
      });

    return { rows, grnsAtPu };
  };

  const handleOpenStatusWorkflow = async (workOrder) => {
    setSelectedWorkOrderForStatus(workOrder);
    setStatusTransitionData({ toStatus: '', comment: '' });
    setWorkOrderStatusHistory([]);
    setWorkOrderCancelBlocked(false);
    setWorkOrderInProgressBlocked(false);
    setCompletionData({
      quantityProduced: workOrder.quantityProduced != null
        ? String(workOrder.quantityProduced)
        : (workOrder.quantityToProduce != null ? String(workOrder.quantityToProduce) : ''),
      laborCostPerUnit: '',
      overheadCostPerUnit: '',
      lineItems: (workOrder.lineItems || []).map((li) => ({
        id: li.id,
        itemId: li.item?.id || null,
        itemName: li.item?.name || '',
        requiredQuantity: li.requiredQuantity != null ? String(li.requiredQuantity) : '',
        consumedQuantity: li.consumedQuantity != null
          ? String(li.consumedQuantity)
          : (li.requiredQuantity != null ? String(li.requiredQuantity) : ''),
        wastageQuantity: li.wastageQuantity != null ? String(li.wastageQuantity) : '',
        uomSymbol: li.unitOfMeasurement?.symbol || li.unitOfMeasurement?.name || '',
        totalIssuedQuantity: '',
        totalReceivedQuantity: ''
      }))
    });
    setShowWorkOrderStatus(true);

    try {
      const response = await axios.get(`/api/production/work-orders/${workOrder.id}/status-history`);
      setWorkOrderStatusHistory(response.data || []);
    } catch (error) {
      toast.error('Error fetching work order status history');
      console.error(error);
    }

    // Fetch GIN/GRN transactions: Total Issued Qty = sum of GINs, Total Received Qty = sum of GRNs at Production Unit
    try {
      const txRes = await axios.get(`/api/production/work-orders/${workOrder.id}/transactions`);
      const data = txRes.data || {};
      setWorkOrderTransactions({ gins: data.gins || [], grns: data.grns || [] });
      setWorkOrderCancelBlocked(hasActiveWorkOrderStockDocs(data));
      setWorkOrderInProgressBlocked(!hasProductionUnitReceiptForWorkOrder(data, workOrder));
      const totalIssuedByItemId = {};
      (data.gins || []).forEach((gin) => {
        if (!gin || !gin.status || gin.status.toUpperCase() !== 'ISSUED') return;
        (gin.lineItems || []).forEach((li) => {
          if (!li || !li.itemId) return;
          const qty = li.issuedQuantity != null ? Number(li.issuedQuantity) : (li.quantity != null ? Number(li.quantity) : 0);
          if (!Number.isFinite(qty)) return;
          totalIssuedByItemId[li.itemId] = (totalIssuedByItemId[li.itemId] || 0) + qty;
        });
      });
      const totalReceivedByItemId = {};
      (data.grns || []).forEach((grn) => {
        if (!grn) return;
        (grn.lineItems || []).forEach((li) => {
          if (!li || !li.itemId) return;
          const qty = li.receivedQuantity != null ? Number(li.receivedQuantity) : (li.quantity != null ? Number(li.quantity) : 0);
          if (!Number.isFinite(qty)) return;
          totalReceivedByItemId[li.itemId] = (totalReceivedByItemId[li.itemId] || 0) + qty;
        });
      });
      setCompletionData((prev) => ({
        ...prev,
        lineItems: (prev.lineItems || []).map((li) => {
          if (!li.itemId) return li;
          const issued = totalIssuedByItemId[li.itemId];
          const received = totalReceivedByItemId[li.itemId];
          return {
            ...li,
            totalIssuedQuantity: issued != null ? issued.toFixed(3) : '',
            totalReceivedQuantity: received != null ? received.toFixed(3) : ''
          };
        })
      }));
    } catch (error) {
      console.error('Error fetching work order transactions for completion details:', error);
      setWorkOrderCancelBlocked(false);
      setWorkOrderInProgressBlocked(false);
    }
  };

  const handleCloseStatusWorkflow = () => {
    setShowWorkOrderStatus(false);
    setSelectedWorkOrderForStatus(null);
    setStatusTransitionData({ toStatus: '', comment: '' });
    setWorkOrderStatusHistory([]);
    setWorkOrderCancelBlocked(false);
    setWorkOrderInProgressBlocked(false);
    setWorkOrderTransactions({ gins: [], grns: [] });
    setCompletionData({ quantityProduced: '', lineItems: [], laborCostPerUnit: '', overheadCostPerUnit: '' });
  };

  /** Weighted average landed cost per unit by material item across GRNs on this work order (MATERIAL lines only). */
  const computeWeightedAvgLandedCostByItemFromGrns = (grns) => {
    const numerator = {};
    const denominator = {};
    (grns || []).forEach((grn) => {
      (grn.lineItems || []).forEach((li) => {
        const lineType = (li.lineType || 'MATERIAL').toString().toUpperCase();
        if (lineType !== 'MATERIAL' || li.itemId == null) return;
        const qty = li.receivedQuantity != null ? Number(li.receivedQuantity) : NaN;
        if (!Number.isFinite(qty) || qty <= 0) return;
        let perUnit = li.landedCostPerUnit != null ? Number(li.landedCostPerUnit) : NaN;
        if (!Number.isFinite(perUnit) && li.landedCost != null) {
          const landed = Number(li.landedCost);
          if (Number.isFinite(landed) && qty > 0) perUnit = landed / qty;
        }
        if (!Number.isFinite(perUnit)) perUnit = 0;
        const id = Number(li.itemId);
        numerator[id] = (numerator[id] || 0) + qty * perUnit;
        denominator[id] = (denominator[id] || 0) + qty;
      });
    });
    const avg = {};
    Object.keys(numerator).forEach((id) => {
      const d = denominator[id];
      avg[Number(id)] = d > 0 ? numerator[id] / d : 0;
    });
    return avg;
  };

  const getCompletionCostPreview = () => {
    const avgByItem = computeWeightedAvgLandedCostByItemFromGrns(workOrderTransactions.grns);
    let rawTotal = 0;
    (completionData.lineItems || []).forEach((li) => {
      if (!li.itemId) return;
      const consumed =
        li.consumedQuantity !== '' && !Number.isNaN(parseFloat(li.consumedQuantity))
          ? parseFloat(li.consumedQuantity)
          : 0;
      const unit = avgByItem[Number(li.itemId)] ?? 0;
      rawTotal += consumed * unit;
    });
    const qtyProduced =
      completionData.quantityProduced !== '' && !Number.isNaN(parseFloat(completionData.quantityProduced))
        ? parseFloat(completionData.quantityProduced)
        : 0;
    const rawPerUnit = qtyProduced > 0 ? rawTotal / qtyProduced : 0;
    const labor =
      completionData.laborCostPerUnit !== '' && !Number.isNaN(parseFloat(completionData.laborCostPerUnit))
        ? parseFloat(completionData.laborCostPerUnit)
        : 0;
    const overhead =
      completionData.overheadCostPerUnit !== '' && !Number.isNaN(parseFloat(completionData.overheadCostPerUnit))
        ? parseFloat(completionData.overheadCostPerUnit)
        : 0;
    const productPerUnit = rawPerUnit + labor + overhead;
    return { rawTotal, rawPerUnit, labor, overhead, productPerUnit };
  };

  const handleSubmitStatusWorkflow = async (e) => {
    e.preventDefault();
    if (!selectedWorkOrderForStatus) return;

    if (!statusTransitionData.toStatus) {
      toast.error('Please select a target status');
      return;
    }
    if (statusTransitionData.toStatus === 'CANCELLED' && workOrderCancelBlocked) {
      toast.error('Cannot cancel work order after goods have been issued or received against it');
      return;
    }
    if (statusTransitionData.toStatus === 'IN_PROGRESS' && workOrderInProgressBlocked) {
      toast.error('Receive materials at the Production Unit (GRN) before moving this work order to IN_PROGRESS');
      return;
    }
    if (!statusTransitionData.comment || !statusTransitionData.comment.trim()) {
      toast.error('Please enter a comment for status transition');
      return;
    }

    if (statusTransitionData.toStatus === 'COMPLETED') {
      if (!completionData.quantityProduced || isNaN(parseFloat(completionData.quantityProduced))) {
        toast.error('Please enter quantity produced for completion');
        return;
      }
      // Consumed + wastage cannot exceed total received qty (from GRNs) per item
      for (const li of completionData.lineItems || []) {
        const consumed = li.consumedQuantity !== '' && !isNaN(parseFloat(li.consumedQuantity)) ? parseFloat(li.consumedQuantity) : 0;
        const wastage = li.wastageQuantity !== '' && !isNaN(parseFloat(li.wastageQuantity)) ? parseFloat(li.wastageQuantity) : 0;
        const totalReceived = li.totalReceivedQuantity !== '' && !isNaN(parseFloat(li.totalReceivedQuantity)) ? parseFloat(li.totalReceivedQuantity) : 0;
        if (consumed + wastage > totalReceived) {
          toast.error(`For item "${li.itemName || 'Unknown'}", consumed + wastage (${(consumed + wastage).toFixed(3)}) cannot exceed total received quantity (${totalReceived.toFixed(3)})`);
          return;
        }
        const required = li.requiredQuantity !== '' && !isNaN(parseFloat(li.requiredQuantity)) ? parseFloat(li.requiredQuantity) : 0;
        if (required > 0 && consumed <= 0) {
          toast.error(`Work order cannot be completed: item "${li.itemName || 'Unknown'}" has required quantity ${required} but consumed quantity is zero. Consumed quantity must be greater than zero when required quantity is greater than zero.`);
          return;
        }
      }
      const laborParsed =
        completionData.laborCostPerUnit !== '' && !Number.isNaN(parseFloat(completionData.laborCostPerUnit))
          ? parseFloat(completionData.laborCostPerUnit)
          : 0;
      const overheadParsed =
        completionData.overheadCostPerUnit !== '' && !Number.isNaN(parseFloat(completionData.overheadCostPerUnit))
          ? parseFloat(completionData.overheadCostPerUnit)
          : 0;
      if (laborParsed < 0 || overheadParsed < 0) {
        toast.error('Labor cost per unit and overhead cost per unit must be zero or greater');
        return;
      }
    }

    try {
      const body = {
        toStatus: statusTransitionData.toStatus,
        comment: statusTransitionData.comment.trim()
      };
      if (statusTransitionData.toStatus === 'COMPLETED') {
        body.quantityProduced = parseFloat(completionData.quantityProduced);
        body.lineItems = (completionData.lineItems || []).map((li) => ({
          id: li.id,
          consumedQuantity:
            li.consumedQuantity !== '' && !isNaN(parseFloat(li.consumedQuantity))
              ? parseFloat(li.consumedQuantity)
              : null,
          wastageQuantity:
            li.wastageQuantity !== '' && !isNaN(parseFloat(li.wastageQuantity))
              ? parseFloat(li.wastageQuantity)
              : null
        }));
        body.laborCostPerUnit =
          completionData.laborCostPerUnit !== '' && !Number.isNaN(parseFloat(completionData.laborCostPerUnit))
            ? parseFloat(completionData.laborCostPerUnit)
            : 0;
        body.overheadCostPerUnit =
          completionData.overheadCostPerUnit !== '' && !Number.isNaN(parseFloat(completionData.overheadCostPerUnit))
            ? parseFloat(completionData.overheadCostPerUnit)
            : 0;
      }

      await axios.post(
        `/api/production/work-orders/${selectedWorkOrderForStatus.id}/status-transition`,
        body
      );
      toast.success('Work order status updated successfully');
      handleCloseStatusWorkflow();
      fetchWorkOrders();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error updating work order status';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error updating work order status');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'DRAFT': 'secondary',
      'APPROVED': 'info',
      'IN_PROGRESS': 'warning',
      'COMPLETED': 'success',
      'CANCELLED': 'danger'
    };
    return badges[status] || 'secondary';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return '0.00';
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return numAmount.toFixed(2);
  };

  /** Per-unit costs: 4–6 decimals (matches backend scale; clearer than 2dp for unit economics). */
  const formatCostPerUnit = (amount) => {
    if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
      return '—';
    }
    const n = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    return n.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 });
  };

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            {/* BOM Tab */}
            <Tab 
              eventKey="boms" 
              title="BOM"
            >
              <FeatureNotice feature="PRODUCTION" />
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Manage Bills of Materials (BOM) - recipes for production</p>
                <Button variant="primary" onClick={handleCreateBOM}>
                  + Create BOM
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <Card>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>BOM Number</th>
                          <th>BOM Name</th>
                          <th>Output Products</th>
                          <th>Version</th>
                          <th>Production Unit</th>
                          <th>Status</th>
                          <th>Default</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {boms.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center text-muted">
                              No BOMs found. Create one to get started.
                            </td>
                          </tr>
                        ) : (
                          boms.map((bom) => (
                            <tr key={bom.id}>
                              <td><strong>{bom.bomNumber}</strong></td>
                              <td>{bom.bomName}</td>
                              <td>
                                {bom.outputProducts && bom.outputProducts.length > 0 ? (
                                  <div>
                                    {bom.outputProducts.map((op, idx) => (
                                      <div key={idx} className="mb-1">
                                        <Badge bg={op.outputType === 'PRIMARY' ? 'primary' : op.outputType === 'CO_PRODUCT' ? 'info' : 'secondary'}>
                                          {op.outputType === 'PRIMARY' ? 'Primary' : op.outputType === 'CO_PRODUCT' ? 'Co-Product' : 'By-Product'}
                                        </Badge>
                                        {' '}
                                        {op.item?.name || '-'}
                                        {op.baseQuantity && ` (${parseFloat(op.baseQuantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })})`}
                                        {op.unitOfMeasurement && ` ${op.unitOfMeasurement.symbol}`}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  bom.finishedGoodItem?.name || '-'
                                )}
                              </td>
                              <td>{bom.version || '1.0'}</td>
                              <td>{bom.productionUnit?.name || '-'}</td>
                              <td>
                                <Badge bg={bom.isActive ? 'success' : 'secondary'}>
                                  {bom.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td>
                                {bom.isDefault && <Badge bg="info">Default</Badge>}
                              </td>
                              <td>
                                <span title="View & Print" style={{ display: 'inline-block' }}>
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleViewPrint(bom, 'BOM')}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                      <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <span title="Edit" style={{ display: 'inline-block' }}>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleEditBOM(bom)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                      <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <span title="Delete" style={{ display: 'inline-block' }}>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteBOM(bom.id)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </Button>
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              {/* BOM Modal */}
              <Modal show={showBOMModal} onHide={handleCloseBOMModal} size="xl">
                <Modal.Header closeButton>
                  <Modal.Title>
                    {editingBOM ? 'Edit BOM' : 'Create BOM'}
                  </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleBOMSubmit}>
                  <Modal.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>BOM Name *</Form.Label>
                          <Form.Control
                            type="text"
                            value={bomFormData.bomName}
                            onChange={(e) => setBomFormData({ ...bomFormData, bomName: e.target.value })}
                            required
                            placeholder="e.g., Product A BOM"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Version</Form.Label>
                          <Form.Control
                            type="text"
                            value={bomFormData.version}
                            onChange={(e) => setBomFormData({ ...bomFormData, version: e.target.value })}
                            placeholder="1.0"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={bomFormData.description}
                        onChange={(e) => setBomFormData({ ...bomFormData, description: e.target.value })}
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Production Unit</Form.Label>
                          <Form.Select
                            value={bomFormData.productionUnitId}
                            onChange={(e) => setBomFormData({ ...bomFormData, productionUnitId: e.target.value })}
                          >
                            <option value="">Select Production Unit (Optional)</option>
                            {productionUnits.map((pu) => (
                              <option key={pu.id} value={pu.id}>
                                {pu.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <hr />
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h6>Output Products *</h6>
                        <Form.Text className="text-muted">
                          Define the products produced by this BOM. At least one PRIMARY product is required.
                        </Form.Text>
                      </div>
                      <Button variant="outline-primary" size="sm" onClick={handleAddBOMOutputProduct}>
                        + Add Output Product
                      </Button>
                    </div>

                    {bomOutputProducts.map((outputProduct, index) => (
                      <Card key={index} className="mb-3">
                        <Card.Body>
                          <Row>
                            <Col md={4}>
                              <Form.Group className="mb-3">
                                <Form.Label>Product Item *</Form.Label>
                                <Form.Select
                                  value={outputProduct.itemId}
                                  onChange={(e) => handleBOMOutputProductChange(index, 'itemId', e.target.value)}
                                  required
                                >
                                  <option value="">Select Product Item</option>
                                  {products
                                    .filter(product => product.item && product.item.isActive)
                                    .map((product) => (
                                      <option key={product.item.id} value={product.item.id}>
                                        {product.item.name} {product.name !== product.item.name ? `(${product.name})` : ''}
                                      </option>
                                    ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-3">
                                <Form.Label>Output Type *</Form.Label>
                                <Form.Select
                                  value={outputProduct.outputType}
                                  onChange={(e) => handleBOMOutputProductChange(index, 'outputType', e.target.value)}
                                  required
                                >
                                  <option value="PRIMARY">Primary Product</option>
                                  <option value="CO_PRODUCT">Co-Product</option>
                                  <option value="BY_PRODUCT">By-Product</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-3">
                                <Form.Label>Base Quantity *</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.001"
                                  value={outputProduct.baseQuantity}
                                  onChange={(e) => handleBOMOutputProductChange(index, 'baseQuantity', e.target.value)}
                                  required
                                  placeholder="0.000"
                                />
                                <Form.Text className="text-muted">
                                  Standard output quantity
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-3">
                                <Form.Label>UOM</Form.Label>
                                <Form.Select
                                  value={outputProduct.unitOfMeasurementId}
                                  onChange={(e) => handleBOMOutputProductChange(index, 'unitOfMeasurementId', e.target.value)}
                                >
                                  <option value="">Select UOM</option>
                                  {uoms.filter(uom => uom.isActive).map((uom) => (
                                    <option key={uom.id} value={uom.id}>
                                      {uom.symbol || uom.name}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={1}>
                              <Form.Group className="mb-3">
                                <Form.Label>&nbsp;</Form.Label>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemoveBOMOutputProduct(index)}
                                  className="w-100"
                                  title="Remove"
                                  disabled={bomOutputProducts.length === 1}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}

                    <Row>
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          label="Active"
                          checked={bomFormData.isActive}
                          onChange={(e) => setBomFormData({ ...bomFormData, isActive: e.target.checked })}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          label="Default BOM for this finished good"
                          checked={bomFormData.isDefault}
                          onChange={(e) => setBomFormData({ ...bomFormData, isDefault: e.target.checked })}
                        />
                      </Col>
                    </Row>

                    <hr />
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>BOM Line Items (Materials/Components)</h6>
                      <Button variant="outline-primary" size="sm" onClick={handleAddBOMLineItem}>
                        + Add Line Item
                      </Button>
                    </div>

                    {bomLineItems.map((lineItem, index) => (
                      <Card key={index} className="mb-3">
                        <Card.Body>
                          <Row>
                            <Col md={4}>
                              <Form.Group className="mb-3">
                                <Form.Label>Item *</Form.Label>
                                <Form.Select
                                  value={lineItem.itemId}
                                  onChange={(e) => handleBOMLineItemChange(index, 'itemId', e.target.value)}
                                  required
                                >
                                  <option value="">Select Item</option>
                                  {organizationMaterials
                                    .filter(orgMaterial => orgMaterial.item && orgMaterial.item.isActive)
                                    .map((orgMaterial) => (
                                      <option key={orgMaterial.item.id} value={orgMaterial.item.id}>
                                        {orgMaterial.item.name} {orgMaterial.sku ? `(${orgMaterial.sku})` : ''}
                                      </option>
                                    ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-3">
                                <Form.Label>Quantity *</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.001"
                                  value={lineItem.quantity}
                                  onChange={(e) => handleBOMLineItemChange(index, 'quantity', e.target.value)}
                                  required
                                  placeholder="0.000"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-3">
                                <Form.Label>UOM</Form.Label>
                                <Form.Select
                                  value={lineItem.unitOfMeasurementId}
                                  onChange={(e) => handleBOMLineItemChange(index, 'unitOfMeasurementId', e.target.value)}
                                >
                                  <option value="">Select UOM</option>
                                  {uoms.map((uom) => (
                                    <option key={uom.id} value={uom.id}>
                                      {uom.symbol || uom.name}
                                    </option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-3">
                                <Form.Label>Wastage %</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.01"
                                  value={lineItem.wastagePercentage}
                                  onChange={(e) => handleBOMLineItemChange(index, 'wastagePercentage', e.target.value)}
                                  placeholder="0.00"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={1}>
                              <Form.Group className="mb-3">
                                <Form.Label>&nbsp;</Form.Label>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemoveBOMLineItem(index)}
                                  className="w-100"
                                  title="Remove"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              </Form.Group>
                            </Col>
                          </Row>
                          <Form.Group className="mb-0">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                              type="text"
                              value={lineItem.notes}
                              onChange={(e) => handleBOMLineItemChange(index, 'notes', e.target.value)}
                              placeholder="Optional notes"
                            />
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    ))}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseBOMModal}>
                      Cancel
                    </Button>
                    <Button variant="primary" type="submit">
                      {editingBOM ? 'Update' : 'Create'}
                    </Button>
                  </Modal.Footer>
                </Form>
              </Modal>
            </Tab>

            {/* Work Orders Tab */}
            <Tab 
              eventKey="work-orders" 
              title="Work Orders"
            >
              <FeatureNotice feature="PRODUCTION" />
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Manage production work orders</p>
                <Button variant="primary" onClick={handleCreateWorkOrder}>
                  + Create Work Order
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : showWorkOrderStatus && selectedWorkOrderForStatus ? (
                <Card>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <Button variant="outline-secondary" onClick={handleCloseStatusWorkflow} className="me-2">
                          ← Back to Work Orders
                        </Button>
                        <h5 className="d-inline-block mb-0 ms-2">Work Order Status Workflow</h5>
                      </div>
                    </div>
                    <Form onSubmit={handleSubmitStatusWorkflow}>
                      {selectedWorkOrderForStatus && (
                        <>
                          <p className="mb-2">
                            <strong>Work Order:</strong> {selectedWorkOrderForStatus.workOrderNumber}
                          </p>
                          <p className="mb-3">
                            <strong>Current Status:</strong>{' '}
                            <Badge bg={getStatusBadge(selectedWorkOrderForStatus.status)}>
                              {selectedWorkOrderForStatus.status}
                            </Badge>
                          </p>
                        </>
                      )}

                      {selectedWorkOrderForStatus && (() => {
                        const { rows, grnsAtPu } = getWorkOrderMaterialStatusSummary();
                        return (
                          <div className="mb-3">
                            <h6 className="mb-2">Materials at Production Unit</h6>
                            {grnsAtPu.length > 0 ? (
                              <p className="text-muted small mb-2">
                                GRNs:{' '}
                                {grnsAtPu.map((g, idx) => (
                                  <span key={g.id != null ? g.id : `grn-${idx}`}>
                                    {idx > 0 ? ', ' : ''}
                                    <strong>{g.grnNumber || '-'}</strong>
                                    {g.grnDate ? ` (${formatDate(g.grnDate)})` : ''}
                                  </span>
                                ))}
                              </p>
                            ) : (
                              <p className="text-muted small mb-2">
                                No GRNs received at the Production Unit for this work order yet.
                              </p>
                            )}
                            {rows.length > 0 ? (
                              <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                <Table striped bordered size="sm" className="mb-0">
                                  <thead>
                                    <tr>
                                      <th>Item</th>
                                      <th>Required</th>
                                      <th>Issued</th>
                                      <th>Received</th>
                                      <th>Consumed</th>
                                      <th>Wastage</th>
                                      <th>Remaining</th>
                                      <th>UOM</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((row) => (
                                      <tr key={row.itemId}>
                                        <td>{row.itemName}</td>
                                        <td>{formatQty3(row.required)}</td>
                                        <td>{formatQty3(row.issued)}</td>
                                        <td>{formatQty3(row.received)}</td>
                                        <td>{formatQty3(row.consumed)}</td>
                                        <td>{formatQty3(row.wastage)}</td>
                                        <td>
                                          <strong>{formatQty3(row.remaining)}</strong>
                                        </td>
                                        <td>{row.uomSymbol || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <p className="text-muted small mb-0">No material lines on this work order.</p>
                            )}
                            <Form.Text className="text-muted">
                              Remaining = Received − Consumed − Wastage (material still on hand at the Production Unit for this WO).
                            </Form.Text>
                          </div>
                        );
                      })()}

                      <Form.Group className="mb-3">
                        <Form.Label>Target Status *</Form.Label>
                        <Form.Select
                          value={statusTransitionData.toStatus}
                          onChange={(e) => setStatusTransitionData({ ...statusTransitionData, toStatus: e.target.value })}
                          required
                        >
                          <option value="">Select Target Status</option>
                          {getAllowedStatusTransitions(selectedWorkOrderForStatus?.status, {
                            blockCancel: workOrderCancelBlocked,
                            blockInProgress: workOrderInProgressBlocked
                          }).map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      {workOrderInProgressBlocked && (selectedWorkOrderForStatus?.status === 'APPROVED') && (
                        <Alert variant="info" className="py-2">
                          IN_PROGRESS is unavailable until materials are received at the Production Unit
                          (create a GRN — Receive at Production Unit against this work order).
                        </Alert>
                      )}

                      {workOrderCancelBlocked && (
                        <Alert variant="warning" className="py-2">
                          Cancel is unavailable because goods have already been issued or received against this work order.
                          Reverse or cancel related GINs/GRNs first, or complete the work order.
                        </Alert>
                      )}

                      <Form.Group className="mb-3">
                        <Form.Label>Comment *</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={statusTransitionData.comment}
                          onChange={(e) => setStatusTransitionData({ ...statusTransitionData, comment: e.target.value })}
                          placeholder="Reason for changing status"
                          required
                        />
                      </Form.Group>

                      {statusTransitionData.toStatus === 'COMPLETED' && selectedWorkOrderForStatus && (
                        <>
                          <h6 className="mt-3">Completion Details</h6>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Quantity to Produce</Form.Label>
                                <Form.Control
                                  type="text"
                                  readOnly
                                  tabIndex={-1}
                                  className="bg-secondary bg-opacity-10"
                                  value={
                                    selectedWorkOrderForStatus.quantityToProduce != null
                                      ? `${parseFloat(selectedWorkOrderForStatus.quantityToProduce).toLocaleString('en-US', {
                                          minimumFractionDigits: 3,
                                          maximumFractionDigits: 3
                                        })}${selectedWorkOrderForStatus.unitOfMeasurement?.symbol || selectedWorkOrderForStatus.unitOfMeasurement?.name ? ` ${selectedWorkOrderForStatus.unitOfMeasurement.symbol || selectedWorkOrderForStatus.unitOfMeasurement.name}` : ''}`
                                      : '-'
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Quantity Produced *</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.001"
                                  value={completionData.quantityProduced}
                                  onChange={(e) =>
                                    setCompletionData({
                                      ...completionData,
                                      quantityProduced: e.target.value
                                    })
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Labor cost / finished unit</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.000001"
                                  min="0"
                                  value={completionData.laborCostPerUnit}
                                  onChange={(e) =>
                                    setCompletionData({ ...completionData, laborCostPerUnit: e.target.value })
                                  }
                                  placeholder="0"
                                />
                                <Form.Text className="text-muted">Optional; organization currency. Defaults to 0.</Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Overhead cost / finished unit</Form.Label>
                                <Form.Control
                                  type="number"
                                  step="0.000001"
                                  min="0"
                                  value={completionData.overheadCostPerUnit}
                                  onChange={(e) =>
                                    setCompletionData({ ...completionData, overheadCostPerUnit: e.target.value })
                                  }
                                  placeholder="0"
                                />
                                <Form.Text className="text-muted">Optional; organization currency. Defaults to 0.</Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                          {(() => {
                            const { rawTotal, rawPerUnit, labor, overhead, productPerUnit } = getCompletionCostPreview();
                            return (
                              <Alert variant="secondary" className="py-2 mb-3">
                                <div className="small text-muted mb-2">
                                  Preview: raw material value uses weighted-average landed cost per item from all GRNs on
                                  this work order × consumed quantities (same calculation as on save). Stored values use
                                  4–6 decimal places on the server.
                                </div>
                                <div>
                                  <strong>Raw material total:</strong> {formatCurrency(rawTotal)}
                                </div>
                                <div>
                                  <strong>Raw material cost / unit:</strong> {formatCostPerUnit(rawPerUnit)}
                                </div>
                                <div>
                                  <strong>Labor / unit:</strong> {formatCostPerUnit(labor)}
                                </div>
                                <div>
                                  <strong>Overhead / unit:</strong> {formatCostPerUnit(overhead)}
                                </div>
                                <div>
                                  <strong>Product cost / unit:</strong> {formatCostPerUnit(productPerUnit)}
                                </div>
                              </Alert>
                            );
                          })()}
                          {completionData.lineItems && completionData.lineItems.length > 0 && (
                            <>
                              <p className="text-muted">
                                Enter actual consumed and wastage quantities for each raw material.
                              </p>
                              <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                <Table striped bordered size="sm" className="mb-0">
                                  <thead>
                                    <tr>
                                      <th>#</th>
                                      <th>Item</th>
                                      <th>Required Qty</th>
                                      <th>Total Issued Qty</th>
                                      <th>Total Received Qty</th>
                                      <th>Consumed Qty</th>
                                      <th>Wastage Qty</th>
                                      <th>UOM</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {completionData.lineItems.map((li, idx) => (
                                      <tr key={li.id || idx}>
                                        <td>{idx + 1}</td>
                                        <td>{li.itemName || '-'}</td>
                                        <td>
                                          {li.requiredQuantity !== ''
                                            ? parseFloat(li.requiredQuantity).toLocaleString('en-US', {
                                                minimumFractionDigits: 3,
                                                maximumFractionDigits: 3
                                              })
                                            : '-'}
                                        </td>
                                        <td>
                                          {li.totalIssuedQuantity !== ''
                                            ? parseFloat(li.totalIssuedQuantity).toLocaleString('en-US', {
                                                minimumFractionDigits: 3,
                                                maximumFractionDigits: 3
                                              })
                                            : '-'}
                                        </td>
                                        <td>
                                          {li.totalReceivedQuantity !== ''
                                            ? parseFloat(li.totalReceivedQuantity).toLocaleString('en-US', {
                                                minimumFractionDigits: 3,
                                                maximumFractionDigits: 3
                                              })
                                            : '-'}
                                        </td>
                                        <td>
                                          <Form.Control
                                            type="number"
                                            step="0.001"
                                            value={li.consumedQuantity}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setCompletionData((prev) => {
                                                const next = { ...prev };
                                                next.lineItems = [...prev.lineItems];
                                                next.lineItems[idx] = {
                                                  ...prev.lineItems[idx],
                                                  consumedQuantity: value
                                                };
                                                return next;
                                              });
                                            }}
                                          />
                                        </td>
                                        <td>
                                          <Form.Control
                                            type="number"
                                            step="0.001"
                                            value={li.wastageQuantity}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setCompletionData((prev) => {
                                                const next = { ...prev };
                                                next.lineItems = [...prev.lineItems];
                                                next.lineItems[idx] = {
                                                  ...prev.lineItems[idx],
                                                  wastageQuantity: value
                                                };
                                                return next;
                                              });
                                            }}
                                          />
                                        </td>
                                        <td>{li.uomSymbol || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </>
                          )}
                        </>
                      )}

                      <h6 className="mt-4">Status History</h6>
                      <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                        <Table striped bordered size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>When</th>
                              <th>From</th>
                              <th>To</th>
                              <th>Comment</th>
                              <th>Changed By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {workOrderStatusHistory.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="text-center text-muted">No status history found.</td>
                              </tr>
                            ) : (
                              workOrderStatusHistory.map((entry) => (
                                <tr key={entry.id}>
                                  <td>{entry.changedAt ? formatDateTime(entry.changedAt) : '-'}</td>
                                  <td>{entry.fromStatus || '-'}</td>
                                  <td>{entry.toStatus}</td>
                                  <td>{entry.commentText || '-'}</td>
                                  <td>{entry.changedByName || entry.changedByEmail || '-'}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>

                      <div className="mt-3 d-flex justify-content-end">
                        <Button
                          variant="warning"
                          type="submit"
                          disabled={getAllowedStatusTransitions(selectedWorkOrderForStatus?.status, {
                            blockCancel: workOrderCancelBlocked,
                            blockInProgress: workOrderInProgressBlocked
                          }).length === 0}
                        >
                          Update Status
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              ) : (
                <Card>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>WO Number</th>
                          <th>Date</th>
                          <th>BOM</th>
                          <th>Finished Good</th>
                          <th>Production Unit</th>
                          <th>Qty to Produce</th>
                          <th>Qty Produced</th>
                          <th>Status</th>
                          <th>Priority</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workOrders.length === 0 ? (
                          <tr>
                            <td colSpan="10" className="text-center text-muted">
                              No work orders found. Create one to get started.
                            </td>
                          </tr>
                        ) : (
                          workOrders.map((wo) => (
                            <tr key={wo.id}>
                              <td><strong>{wo.workOrderNumber}</strong></td>
                              <td>{formatDate(wo.workOrderDate)}</td>
                              <td>{wo.bom?.bomName || '-'}</td>
                              <td>
                                {wo.bom?.outputProducts && wo.bom.outputProducts.length > 0 ? (
                                  <div>
                                    {wo.bom.outputProducts
                                      .filter(op => op.outputType === 'PRIMARY')
                                      .map((op, idx) => (
                                        <div key={idx}>
                                          {op.item?.name || '-'}
                                          {op.baseQuantity && ` (${parseFloat(op.baseQuantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })})`}
                                          {op.unitOfMeasurement && ` ${op.unitOfMeasurement.symbol}`}
                                        </div>
                                      ))}
                                    {wo.bom.outputProducts.filter(op => op.outputType === 'PRIMARY').length === 0 && '-'}
                                  </div>
                                ) : (
                                  wo.bom?.finishedGoodItem?.name || '-'
                                )}
                              </td>
                              <td>{wo.productionUnit?.name || '-'}</td>
                              <td>
                                {parseFloat(wo.quantityToProduce).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                                {wo.unitOfMeasurement && ` ${wo.unitOfMeasurement.symbol}`}
                              </td>
                              <td>{parseFloat(wo.quantityProduced || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                              <td>
                                <Badge bg={getStatusBadge(wo.status)}>
                                  {wo.status}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg={wo.priority === 'URGENT' ? 'danger' : wo.priority === 'HIGH' ? 'warning' : 'secondary'}>
                                  {wo.priority}
                                </Badge>
                              </td>
                              <td>
                                <span title="View & Print" style={{ display: 'inline-block' }}>
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleViewPrint(wo, 'WORK_ORDER')}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                      <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <span title={wo.status !== 'DRAFT' && wo.status !== 'APPROVED' ? 'Edit (available only for DRAFT or APPROVED)' : 'Edit'} style={{ display: 'inline-block' }}>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleEditWorkOrder(wo)}
                                    disabled={wo.status !== 'DRAFT' && wo.status !== 'APPROVED'}
                                    style={(wo.status !== 'DRAFT' && wo.status !== 'APPROVED') ? { pointerEvents: 'none' } : undefined}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                      <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <span title={getAllowedStatusTransitions(wo.status).length === 0 ? 'Status Workflow (no transitions available)' : 'Status Workflow'} style={{ display: 'inline-block' }}>
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleOpenStatusWorkflow(wo)}
                                    disabled={getAllowedStatusTransitions(wo.status).length === 0}
                                    style={getAllowedStatusTransitions(wo.status).length === 0 ? { pointerEvents: 'none' } : undefined}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M8 3a5 5 0 1 1-4.546 2.916.5.5 0 1 1 .908.418A4 4 0 1 0 8 4V2.5a.5.5 0 0 1 1 0v2A.5.5 0 0 1 8.5 5h-2a.5.5 0 0 1 0-1H7V3z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <span title={wo.status !== 'DRAFT' ? 'Delete (available only for DRAFT)' : 'Delete'} style={{ display: 'inline-block' }}>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteWorkOrder(wo.id)}
                                    disabled={wo.status !== 'DRAFT'}
                                    style={wo.status !== 'DRAFT' ? { pointerEvents: 'none' } : undefined}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <HistoryButton documentType="WorkOrder" documentId={wo.id} label={`Work order ${wo.workOrderNumber}`} className="ms-1" />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              {/* Work Order Modal */}
              <Modal show={showWorkOrderModal} onHide={handleCloseWorkOrderModal} size="lg">
                <Modal.Header closeButton>
                  <Modal.Title>
                    {editingWorkOrder ? 'Edit Work Order' : 'Create Work Order'}
                  </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleWorkOrderSubmit}>
                  <Modal.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Work Order Date *</Form.Label>
                          <DateInput
                           
                            value={workOrderFormData.workOrderDate}
                            max={
                              editWorkOrderHasActivity && editWorkOrderEarliestMovement
                                ? (editWorkOrderEarliestMovement < todayISO()
                                    ? editWorkOrderEarliestMovement
                                    : todayISO())
                                : todayISO()
                            }
                            onChange={(e) => setWorkOrderFormData({ ...workOrderFormData, workOrderDate: e.target.value })}
                            required
                          />
                          <Form.Text className="text-muted">
                            {editWorkOrderHasActivity && editWorkOrderEarliestMovement
                              ? `Can be backdated, but not after the first material movement (${editWorkOrderEarliestMovement})`
                              : 'Can be backdated; future dates are not allowed'}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Priority</Form.Label>
                          <Form.Select
                            value={workOrderFormData.priority}
                            onChange={(e) => setWorkOrderFormData({ ...workOrderFormData, priority: e.target.value })}
                          >
                            <option value="LOW">LOW</option>
                            <option value="NORMAL">NORMAL</option>
                            <option value="HIGH">HIGH</option>
                            <option value="URGENT">URGENT</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>BOM *</Form.Label>
                          <Form.Select
                            value={workOrderFormData.bomId}
                            onChange={(e) => setWorkOrderFormData({ ...workOrderFormData, bomId: e.target.value })}
                            required
                            disabled={!!editingWorkOrder}
                          >
                            <option value="">Select BOM</option>
                            {boms.filter(bom => bom.isActive).map((bom) => (
                              <option key={bom.id} value={bom.id}>
                                {bom.bomName} - {bom.finishedGoodItem?.name || ''}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Production Unit *</Form.Label>
                          <Form.Select
                            value={workOrderFormData.productionUnitId}
                            onChange={(e) => setWorkOrderFormData({ ...workOrderFormData, productionUnitId: e.target.value })}
                            required
                            disabled={editWorkOrderHasActivity}
                          >
                            <option value="">Select Production Unit</option>
                            {productionUnits.map((pu) => (
                              <option key={pu.id} value={pu.id}>
                                {pu.name}
                              </option>
                            ))}
                          </Form.Select>
                          {editWorkOrderHasActivity && (
                            <Form.Text className="text-muted">
                              Locked: material has already been issued or received against this work order.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Quantity to Produce *</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.001"
                            value={workOrderFormData.quantityToProduce}
                            onChange={(e) => setWorkOrderFormData({ ...workOrderFormData, quantityToProduce: e.target.value })}
                            required
                            disabled={!!editingWorkOrder}
                            placeholder="0.000"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Unit of Measurement</Form.Label>
                          <Form.Select
                            value={workOrderFormData.unitOfMeasurementId}
                            onChange={(e) => setWorkOrderFormData({ ...workOrderFormData, unitOfMeasurementId: e.target.value })}
                            disabled={!!editingWorkOrder}
                          >
                            <option value="">Select UOM</option>
                            {uoms.filter(uom => uom.isActive).map((uom) => (
                              <option key={uom.id} value={uom.id}>
                                {uom.symbol} - {uom.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Date</Form.Label>
                          <DateInput
                           
                            value={workOrderFormData.startDate}
                            onChange={(e) => setWorkOrderFormData({ ...workOrderFormData, startDate: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Completion Date</Form.Label>
                          <DateInput
                           
                            value={workOrderFormData.completionDate}
                            onChange={(e) => setWorkOrderFormData({ ...workOrderFormData, completionDate: e.target.value })}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={workOrderFormData.notes}
                        onChange={(e) => setWorkOrderFormData({ ...workOrderFormData, notes: e.target.value })}
                        placeholder="Additional notes about this work order"
                      />
                    </Form.Group>

                    {editingWorkOrder && editingWorkOrder.lineItems && editingWorkOrder.lineItems.length > 0 && (
                      <div className="mt-3">
                        <h6>Material Consumption</h6>
                        <Table striped bordered size="sm">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Required</th>
                              <th>Issued</th>
                              <th>Received</th>
                              <th>Consumed</th>
                              <th>Wastage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {editingWorkOrder.lineItems.map((li, idx) => {
                              const itemId = li.item?.id;
                              const issued = itemId != null ? editWorkOrderTotals.issued[itemId] : undefined;
                              const received = itemId != null ? editWorkOrderTotals.received[itemId] : undefined;
                              return (
                                <tr key={idx}>
                                  <td>{li.item?.name || '-'}</td>
                                  <td>{parseFloat(li.requiredQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                                  <td>{(issued != null ? issued : 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                                  <td>{(received != null ? received : 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                                  <td>{parseFloat(li.consumedQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                                  <td>{parseFloat(li.wastageQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        <Form.Text className="text-muted">
                          Issued = materials issued to production (GINs); Received = materials received for this work order (GRNs, excluding cancelled/reversed).
                        </Form.Text>
                      </div>
                    )}
                  </Modal.Body>
                  <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseWorkOrderModal}>
                      Cancel
                    </Button>
                    <Button variant="primary" type="submit">
                      {editingWorkOrder ? 'Update' : 'Create'}
                    </Button>
                  </Modal.Footer>
                </Form>
              </Modal>
            </Tab>
          
            {/* Job work: challans for goods sent to job workers, returns, ITC-04 */}
            <Tab
              eventKey="job-work"
              title="Job Work"
            >
              <FeatureNotice feature="JOB_WORK" />
              <JobWork active={activeTab === 'job-work'} />
            </Tab>
          </Tabs>
        </Col>
      </Row>

      {/* Print View Modal */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} size="xl" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>
            {printType === 'BOM' && 'Bill of Materials'}
            {printType === 'WORK_ORDER' && 'Work Order'}
          </Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
          {printType === 'BOM' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>BILL OF MATERIALS</h2>
                <p className="text-muted">BOM: <strong>{printData.bomNumber}</strong> — {printData.bomName}</p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Version:</strong> {printData.version || '-'}</p>
                  <p><strong>Production Unit:</strong> {printData.productionUnit?.name || '-'}</p>
                  <p><strong>Active:</strong> {printData.isActive ? 'Yes' : 'No'}</p>
                </Col>
                <Col md={6}>
                  {printData.description && <p><strong>Description:</strong> {printData.description}</p>}
                </Col>
              </Row>
              {printData.outputProducts && printData.outputProducts.length > 0 && (
                <>
                  <h5 className="mt-3 mb-2">Output Products</h5>
                  <Table striped bordered size="sm">
                    <thead><tr><th>#</th><th>Type</th><th>Item</th><th>Base Qty</th><th>UOM</th></tr></thead>
                    <tbody>
                      {printData.outputProducts.map((op, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{op.outputType || '-'}</td>
                          <td>{op.item?.name || '-'}</td>
                          <td>{op.baseQuantity != null ? parseFloat(op.baseQuantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '-'}</td>
                          <td>{op.unitOfMeasurement?.symbol || op.unitOfMeasurement?.name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
              {printData.lineItems && printData.lineItems.length > 0 && (
                <>
                  <h5 className="mt-3 mb-2">Line Items</h5>
                  <Table striped bordered size="sm">
                    <thead><tr><th>#</th><th>Item</th><th>Quantity</th><th>UOM</th><th>Wastage %</th><th>Notes</th></tr></thead>
                    <tbody>
                      {printData.lineItems.map((li, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{li.item?.name || '-'}</td>
                          <td>{li.quantity != null ? parseFloat(li.quantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '-'}</td>
                          <td>{li.unitOfMeasurement?.symbol || li.unitOfMeasurement?.name || '-'}</td>
                          <td>{li.wastagePercentage != null ? parseFloat(li.wastagePercentage).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                          <td>{li.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </div>
          )}
          {printType === 'WORK_ORDER' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>WORK ORDER</h2>
                <p className="text-muted">WO: <strong>{printData.workOrderNumber}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Date:</strong> {formatDate(printData.workOrderDate)}</p>
                  <p><strong>BOM:</strong> {printData.bom?.bomNumber || '-'} ({printData.bom?.bomName || '-'})</p>
                  <p><strong>Production Unit:</strong> {printData.productionUnit?.name || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Quantity to Produce:</strong> {printData.quantityToProduce != null ? parseFloat(printData.quantityToProduce).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '-'} {printData.unitOfMeasurement?.symbol || ''}</p>
                  <p><strong>Quantity Produced:</strong> {printData.quantityProduced != null ? parseFloat(printData.quantityProduced).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '-'}</p>
                  <p><strong>Status:</strong> {printData.status}</p>
                  <p><strong>Priority:</strong> {printData.priority || '-'}</p>
                  {printData.status === 'COMPLETED' &&
                    (printData.productCostPerUnit != null ||
                      printData.rawMaterialCostPerUnit != null ||
                      printData.rawMaterialTotalCost != null) && (
                      <>
                        <p className="mt-2 mb-1">
                          <strong>Completion costing</strong>
                        </p>
                        <p className="mb-0">
                          <strong>Raw material total:</strong>{' '}
                          {printData.rawMaterialTotalCost != null ? formatCurrency(printData.rawMaterialTotalCost) : '—'}
                        </p>
                        <p className="mb-0">
                          <strong>Raw material cost / unit:</strong>{' '}
                          {printData.rawMaterialCostPerUnit != null
                            ? formatCostPerUnit(printData.rawMaterialCostPerUnit)
                            : '—'}
                        </p>
                        <p className="mb-0">
                          <strong>Labor cost / unit:</strong>{' '}
                          {printData.laborCostPerUnit != null ? formatCostPerUnit(printData.laborCostPerUnit) : '—'}
                        </p>
                        <p className="mb-0">
                          <strong>Overhead cost / unit:</strong>{' '}
                          {printData.overheadCostPerUnit != null ? formatCostPerUnit(printData.overheadCostPerUnit) : '—'}
                        </p>
                        <p className="mb-0">
                          <strong>Product cost / unit:</strong>{' '}
                          {printData.productCostPerUnit != null ? formatCostPerUnit(printData.productCostPerUnit) : '—'}
                        </p>
                      </>
                    )}
                </Col>
              </Row>
              {printData.lineItems && printData.lineItems.length > 0 && (
                <>
                  <h5 className="mt-3 mb-2">Line Items</h5>
                  <Table striped bordered size="sm">
                    <thead><tr><th>Item</th><th>Required Qty</th><th>Total Issued Qty</th><th>Total Received Qty</th><th>Consumed</th><th>Wastage</th></tr></thead>
                    <tbody>
                      {printData.lineItems.map((li, idx) => {
                        const uom = li.unitOfMeasurement?.symbol || li.unitOfMeasurement?.name || '';
                        const totalIssuedQty = (workOrderTransactions.gins || []).reduce((sum, gin) => {
                          const ginLineItems = gin.lineItems || [];
                          const issuedForItem = ginLineItems.reduce((itemSum, ginLi) => {
                            const sameItem = ginLi.itemId != null && li.item?.id != null && Number(ginLi.itemId) === Number(li.item.id);
                            if (!sameItem) return itemSum;
                            const issued = ginLi.issuedQuantity != null ? Number(ginLi.issuedQuantity) : (ginLi.quantity != null ? Number(ginLi.quantity) : 0);
                            return itemSum + (Number.isFinite(issued) ? issued : 0);
                          }, 0);
                          return sum + issuedForItem;
                        }, 0);
                        const totalReceivedQty = (workOrderTransactions.grns || []).reduce((sum, grn) => {
                          const grnLineItems = grn.lineItems || [];
                          const receivedForItem = grnLineItems.reduce((itemSum, grnLi) => {
                            const sameItem = grnLi.itemId != null && li.item?.id != null && Number(grnLi.itemId) === Number(li.item.id);
                            if (!sameItem) return itemSum;
                            const received = grnLi.receivedQuantity != null ? Number(grnLi.receivedQuantity) : (grnLi.quantity != null ? Number(grnLi.quantity) : 0);
                            return itemSum + (Number.isFinite(received) ? received : 0);
                          }, 0);
                          return sum + receivedForItem;
                        }, 0);
                        return (
                        <tr key={idx}>
                          <td>{li.item?.name || '-'}</td>
                          <td>{parseFloat(li.requiredQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}{uom ? ` ${uom}` : ''}</td>
                          <td>{totalIssuedQty.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}{uom ? ` ${uom}` : ''}</td>
                          <td>{totalReceivedQty.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}{uom ? ` ${uom}` : ''}</td>
                          <td>{parseFloat(li.consumedQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}{uom ? ` ${uom}` : ''}</td>
                          <td>{parseFloat(li.wastageQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}{uom ? ` ${uom}` : ''}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}
              {printData.notes && <p className="mt-3"><strong>Notes:</strong> {printData.notes}</p>}

              {/* GINs and GRNs against this Work Order */}
              {loadingWorkOrderTransactions && <p className="mt-3 text-muted">Loading transactions…</p>}
              {!loadingWorkOrderTransactions && (workOrderTransactions.gins?.length > 0 || workOrderTransactions.grns?.length > 0) && (
                <>
                  {workOrderTransactions.gins?.length > 0 && (
                    <div className="mt-4">
                      <h5 className="mb-2">Goods Issue Notes (GINs)</h5>
                      {workOrderTransactions.gins.map((gin) => (
                        <div key={gin.id} className="mb-4">
                          <p className="mb-1"><strong>{gin.ginNumber}</strong> — {formatDate(gin.ginDate)} — {gin.status} — {gin.locationName || '-'}</p>
                          <Table striped bordered size="sm">
                            <thead><tr><th>#</th><th>Item</th><th>Requested Qty</th><th>Issue Qty</th><th>Remarks</th></tr></thead>
                            <tbody>
                              {(gin.lineItems || []).map((li, idx) => (
                                <tr key={li.id || idx}>
                                  <td>{idx + 1}</td>
                                  <td>{li.itemName || '-'}</td>
                                  <td>
                                    {li.quantity != null
                                      ? `${parseFloat(li.quantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}${li.uomSymbol ? ` ${li.uomSymbol}` : ''}`
                                      : '-'}
                                  </td>
                                  <td>
                                    {li.issuedQuantity != null
                                      ? `${parseFloat(li.issuedQuantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}${li.uomSymbol ? ` ${li.uomSymbol}` : ''}`
                                      : '-'}
                                  </td>
                                  <td>{li.remarks || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  )}
                  {workOrderTransactions.grns?.length > 0 && (
                    <div className="mt-4">
                      <h5 className="mb-2">Goods Receipt Notes (GRNs)</h5>
                      {workOrderTransactions.grns.map((grn) => (
                        <div key={grn.id} className="mb-4">
                          <p className="mb-1"><strong>{grn.grnNumber}</strong> — {formatDate(grn.grnDate)} — {grn.status} — {grn.locationName || '-'}</p>
                          <Table striped bordered size="sm">
                            <thead><tr><th>#</th><th>Type</th><th>Item</th><th>Received Qty</th><th>Unit Price</th><th>Line Total</th><th>UOM</th><th>Lot / Ref</th></tr></thead>
                            <tbody>
                              {(grn.lineItems || []).map((li, idx) => (
                                <tr key={li.id || idx}>
                                  <td>{idx + 1}</td>
                                  <td>{li.lineType || 'MATERIAL'}</td>
                                  <td>{li.itemName || '-'}</td>
                                  <td>{li.receivedQuantity != null ? parseFloat(li.receivedQuantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '-'}</td>
                                  <td>{li.unitPrice != null ? parseFloat(li.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                                  <td>{li.lineTotal != null ? parseFloat(li.lineTotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                                  <td>{li.uomSymbol || '-'}</td>
                                  <td>{(li.vendorLotNumber || li.internalLotNumber || '-')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="mt-4">
                <h5 className="mb-2">Work Order Change Log</h5>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>From</th>
                      <th>To</th>
                      <th>Comment</th>
                      <th>Changed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workOrderPrintStatusHistory.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted">No change log found.</td>
                      </tr>
                    ) : (
                      workOrderPrintStatusHistory.map((entry) => (
                        <tr key={entry.id}>
                          <td>{entry.changedAt ? formatDateTime(entry.changedAt) : '-'}</td>
                          <td>{entry.fromStatus || '-'}</td>
                          <td>{entry.toStatus || '-'}</td>
                          <td>{entry.commentText || '-'}</td>
                          <td>{entry.changedByName || entry.changedByEmail || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
                  </OrgPrintChrome>
        </Modal.Body>
      </Modal>

      {/* Help Pane */}
    </Container>
  );
}

export default Production;

